import React from "react";
import { useTranslation } from "react-i18next";

/**
 * MobileComparison - Versión móvil optimizada
 * - Sin slider interactivo (demasiado complejo para móvil)
 * - Comparación lado a lado simplificada
 * - Sin Aurora ni efectos pesados
 */
const MobileComparison: React.FC = () => {
  const { t } = useTranslation();

  const withoutVett = [
    t("comparison.withoutList.1", "Manual record keeping"),
    t("comparison.withoutList.2", "Time-consuming research"),
    t("comparison.withoutList.3", "Limited collaboration"),
    t("comparison.withoutList.4", "Isolated practice"),
  ];

  const withVett = [
    t("comparison.withList.1", "Automated SOAP notes"),
    t("comparison.withList.2", "Instant AI-powered insights"),
    t("comparison.withList.3", "Global network access"),
    t("comparison.withList.4", "24/7 expert support"),
  ];

  return (
    <section className="relative py-16 px-4">
      <div className="container mx-auto max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-[#00342d] mb-4">
            {t("comparison.title", "Transform Your Veterinary Practice")}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t(
              "comparison.subtitle",
              "See the difference VettConsult makes in your daily practice"
            )}
          </p>
        </div>

        {/* Comparison Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Without VettConsult */}
          <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-700">
                {t("comparison.withoutTitle", "Without VettConsult")}
              </h3>
            </div>

            <ul className="space-y-3">
              {withoutVett.map((item, index) => (
                <li
                  key={index}
                  className="flex items-start gap-3 text-gray-600"
                >
                  <span className="text-gray-400 mt-1">✗</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* With VettConsult */}
          <div className="bg-white rounded-2xl p-6 border-2 border-[#00e9be] shadow-lg relative overflow-hidden">
            {/* Geometric Accent */}
            <div className="absolute top-0 right-0 w-20 h-20 bg-[#e6fffa] rounded-bl-full -mr-6 -mt-6 z-0" />

            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#e6fffa] rounded-full flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-[#00e9be]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-[#00342d]">
                  {t("comparison.withTitle", "With VettConsult")}
                </h3>
              </div>

              <ul className="space-y-3">
                {withVett.map((item, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-3 text-gray-700 font-medium"
                  >
                    <span className="text-[#00e9be] mt-1">✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <p className="text-gray-500 text-sm">
            {t(
              "comparison.cta",
              "Join manys of veterinarians who have transformed their practice"
            )}
          </p>
        </div>
      </div>
    </section>
  );
};

export default MobileComparison;
